import React, { useEffect, useState, useCallback } from "react";
import { ArrowsClockwise, ArrowDown, ChartLineUp, Warning } from "@phosphor-icons/react";
import { api, fmtAone, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLivePrice } from "@/context/LivePriceContext";
import { toast } from "sonner";

function AssetPill({ symbol, active, onClick }) {
    return (
        <button onClick={onClick} className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${active ? "border-primary/60 bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold font-mono ${symbol === "AONE" ? "bg-gradient-to-br from-primary to-violet-600 text-[var(--text)]" : "bg-gradient-to-br from-emerald-400 to-emerald-600 text-[var(--text)]"}`}>
                {symbol[0]}
            </div>
            <span className="font-display text-[13px] font-semibold">{symbol}</span>
        </button>
    );
}

export default function SwapPage({ onConnect }) {
    const { user, refresh } = useAuth();
    const { price: livePrice } = useLivePrice();
    const [pool, setPool] = useState(null);
    const [trades, setTrades] = useState([]);
    const [from, setFrom] = useState("USDT");
    const [to, setTo] = useState("AONE");
    const [amount, setAmount] = useState("");
    const [slippage, setSlippage] = useState(50);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadPool = useCallback(async () => {
        try {
            const [p, t] = await Promise.all([api.get("/swap/pool"), api.get("/swap/trades?limit=15")]);
            setPool(p.data); setTrades(t.data);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        loadPool();
        const iv = setInterval(loadPool, 4000);
        return () => clearInterval(iv);
    }, [loadPool]);

    useEffect(() => {
        if (!amount || Number(amount) <= 0) return setQuote(null);
        const c = new AbortController();
        (async () => {
            try {
                const { data } = await api.post("/swap/quote", {
                    from_asset: from, to_asset: to, amount_in: Number(amount), slippage_bps: slippage,
                }, { signal: c.signal });
                setQuote(data);
            } catch (e) { if (!c.signal.aborted) setQuote(null); }
        })();
        return () => c.abort();
    }, [amount, from, to, slippage]);

    const flip = () => { setFrom(to); setTo(from); setAmount(""); setQuote(null); };

    const balance = from === "AONE" ? user?.aone_balance : user?.usdt_balance;

    const swap = async (e) => {
        e.preventDefault();
        if (!user) return onConnect();
        setBusy(true);
        try {
            await api.post("/swap/execute", {
                from_asset: from, to_asset: to, amount_in: Number(amount), slippage_bps: slippage,
            });
            toast.success(`Swapped ${amount} ${from} → ${quote?.amount_out?.toFixed(4)} ${to}`);
            setAmount(""); setQuote(null);
            await refresh(); await loadPool();
        } catch (e) { toast.error(formatApiError(e, "Swap failed")); }
        finally { setBusy(false); }
    };

    if (!pool) return <div className="p-20 text-center text-muted">Loading pool…</div>;

    return (
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                        <div className="eyebrow mb-2">Aegis Swap</div>
                        <h1 className="font-display text-[26px] sm:text-[32px] lg:text-[42px] font-semibold leading-tight break-words">
                            AONE <span className="text-[var(--text-3)]">⇄</span> <span className="gradient-text font-bold">USDT</span>
                        </h1>
                        <p className="text-muted mt-3 text-[13px] sm:text-[14px] max-w-[560px]">
                            Constant-product AMM (x·y=k) with 0.3% fee. Swap USDT for AONE to pay gas fees on the Aegis L1, or convert your mined AONE back to USDT anytime.
                        </p>
                    </div>
                    <div className="card p-4 text-right shrink-0">
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Live AONE / USDT</div>
                        <div data-testid="swap-live-price" className="font-mono text-[22px] font-semibold text-[var(--primary-glow)] tabular-nums mt-1">
                            ${livePrice.toFixed(6)}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--success)] mt-1">Ticking · 100ms</div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="card-elevated p-4"><div className="eyebrow">Mid price</div>
                        <div className="font-mono text-[20px] mt-1 text-primary">${pool.price_aone_usdt.toFixed(4)}</div>
                        <div className="text-[10px] font-mono text-muted">per AONE</div></div>
                    <div className="card-elevated p-4"><div className="eyebrow">TVL</div>
                        <div className="font-mono text-[20px] mt-1 text-gold">${(pool.tvl_usdt / 1000).toFixed(1)}K</div>
                        <div className="text-[10px] font-mono text-muted">USDT depth</div></div>
                    <div className="card-elevated p-4"><div className="eyebrow">AONE reserve</div>
                        <div className="font-mono text-[20px] mt-1">{fmtAone(pool.aone_reserve)}</div>
                        <div className="text-[10px] font-mono text-muted">in pool</div></div>
                    <div className="card-elevated p-4"><div className="eyebrow">Fee</div>
                        <div className="font-mono text-[20px] mt-1">{(pool.fee_bps / 100).toFixed(2)}%</div>
                        <div className="text-[10px] font-mono text-muted">to LPs</div></div>
                </div>

                {/* Recent trades */}
                <div className="panel p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <ChartLineUp size={18} className="text-primary" />
                        <div className="font-display text-[16px] sm:text-[18px] font-semibold">Recent trades</div>
                    </div>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <table className="tbl min-w-[560px]">
                        <thead><tr>
                            <th>Direction</th><th className="text-right">In</th><th className="text-right">Out</th>
                            <th className="text-right">Fee</th><th>Hash</th>
                        </tr></thead>
                        <tbody>
                            {trades.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-6">No trades yet - be the first.</td></tr>}
                            {trades.map((t) => (
                                <tr key={t.tx_hash} data-testid={`swap-trade-${t.tx_hash.slice(0,10)}`}>
                                    <td><span className="chip !text-[10px] !py-0">{t.from_asset} → {t.to_asset}</span></td>
                                    <td className="text-right font-mono">{Number(t.amount_in).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                                    <td className="text-right font-mono text-primary">{Number(t.amount_out).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                                    <td className="text-right font-mono text-muted">{Number(t.fee).toFixed(4)}</td>
                                    <td className="font-mono text-[11px]">{t.tx_hash.slice(0, 12)}…</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            {/* SWAP CARD */}
            <div className="lg:col-span-4">
                <div className="card-elevated p-5 sm:p-6 lg:sticky lg:top-24">
                    <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                        <div className="font-display text-[18px] sm:text-[22px] font-semibold">Swap</div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {[10, 50, 100, 300].map((b) => (
                                <button key={b} onClick={() => setSlippage(b)} data-testid={`swap-slippage-${b}`}
                                    className={`text-[10.5px] font-mono px-2 py-1 rounded-md border transition-colors ${slippage === b ? "border-primary text-primary bg-primary/10" : "border-[var(--border)] text-muted hover:text-[var(--text)]"}`}>
                                    {(b / 100).toFixed(b < 100 ? 2 : 1)}%
                                </button>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={swap} className="space-y-3">
                        <div className="panel p-4">
                            <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted mb-2">
                                <span>You pay</span>
                                {user && <span>Balance: <span className="text-primary">{(balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></span>}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    data-testid="swap-amount-input"
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="0.0"
                                    className="flex-1 bg-transparent font-mono text-[20px] sm:text-[26px] focus:outline-none min-w-0 w-full"
                                />
                                <AssetPill symbol={from} active />
                            </div>
                            {user && (
                                <button type="button" onClick={() => setAmount(String(balance || 0))} className="text-[10px] font-mono text-primary mt-2 hover:underline">
                                    Use max
                                </button>
                            )}
                        </div>

                        <div className="flex justify-center">
                            <button type="button" onClick={flip} data-testid="swap-flip"
                                className="w-9 h-9 rounded-full border border-white/10 bg-[var(--bg-surface)] flex items-center justify-center hover:border-primary hover:text-primary transition-colors">
                                <ArrowDown size={14} />
                            </button>
                        </div>

                        <div className="panel p-4">
                            <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted mb-2">
                                <span>You receive</span>
                                <span>Min: {quote ? Number(quote.min_out).toFixed(4) : "-"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 font-mono text-[20px] sm:text-[26px] text-primary truncate">
                                    {quote ? quote.amount_out.toFixed(4) : "0.0"}
                                </div>
                                <AssetPill symbol={to} active />
                            </div>
                        </div>

                        {quote && (
                            <div className="text-[11.5px] font-mono text-muted space-y-1 px-1">
                                <div className="flex justify-between"><span>Rate</span><span className="text-[var(--text)]">1 {from} = {(quote.amount_out / quote.amount_in).toFixed(6)} {to}</span></div>
                                <div className="flex justify-between"><span>Price impact</span>
                                    <span className={quote.price_impact_pct > 5 ? "text-rose-400" : quote.price_impact_pct > 1 ? "text-gold" : "text-mint"}>
                                        {quote.price_impact_pct.toFixed(3)}%
                                    </span>
                                </div>
                                <div className="flex justify-between"><span>LP fee</span><span>{quote.fee.toFixed(4)} {from}</span></div>
                            </div>
                        )}

                        {quote && quote.price_impact_pct > 5 && (
                            <div className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/[0.05] rounded-md">
                                <Warning size={13} className="text-rose-400 flex-shrink-0 mt-0.5" />
                                <div className="text-[11.5px] text-rose-300">High price impact - reduce trade size or increase slippage tolerance.</div>
                            </div>
                        )}

                        <button
                            data-testid="swap-submit"
                            type="submit"
                            disabled={busy || !amount || !quote}
                            className="btn-primary w-full inline-flex items-center justify-center gap-2 mt-2"
                        >
                            <ArrowsClockwise size={13} weight="bold" /> {user ? (busy ? "Swapping…" : "Swap") : "Connect to swap"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
